import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/main/authSlice";
import { apiService } from "../../utils/apiService";
import Toastify from 'toastify-js';
import "toastify-js/src/toastify.css";
import moment from 'moment';
import 'moment/locale/it'; // Importa la localizzazione italiana per moment
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faCalendarAlt, faExclamationTriangle, faTags, faCreditCard, faTools, faShieldAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const CustomerAssistanceRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const user = useSelector(selectUser);
  
  // Inizializza moment con la localizzazione italiana
  moment.locale('it');
  
  // Effetto che si attiva quando cambia l'utente o al caricamento iniziale
  useEffect(() => {
    document.title = "Richieste di Assistenza | Installamelo";
    if (user) {
      loadAssistanceRequests();
    }
  }, [user]);

  const loadAssistanceRequests = async () => {
    try {
      setLoading(true);
      
      // Cerca di ottenere i dati sia da Redux che da localStorage
      let accountId, customerUserId, customerId;
      
      if (user?.accountId && user?.customerUserId) {
        // Priorità ai dati dallo stato Redux
        accountId = user.accountId;
        customerUserId = user.customerUserId;
        customerId = user.customerId;
      } else {
        // Fallback su localStorage
        const userData = JSON.parse(localStorage.getItem('installamelo-auth-user'));
        accountId = userData?.accountId;
        customerUserId = userData?.customerUserId;
        customerId = userData?.customerId;
      }
      
      // Verifica che customerUserId e accountId siano disponibili
      if (!customerUserId || !accountId) {
        console.error("Dati utente mancanti per caricare le richieste di assistenza");
        Toastify({
          text: "Impossibile caricare i dati: informazioni cliente mancanti",
          duration: 3000,
          close: true,
          gravity: "top",
          position: "right",
          style: { background: "#f44336" },
        }).showToast();
        setLoading(false);
        return;
      }
      
      // Utilizzo dell'endpoint assistanceRequests/list con accountId e customerId
      const response = await apiService.get(
        `${import.meta.env.VITE_API_BASE_URL}/assistanceRequests/list?accountId=${accountId}&customerId=${customerId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'auth-token': localStorage.getItem('installamelo-auth-token')
          }
        }
      );
      
      const responseData = response.data || [];
      
      // Ordina le richieste per data di creazione (più recenti prima)
      const sortedData = responseData.sort((a, b) => {
        const dateA = new Date(a.creationDate || a.createdAt || a.created_at || 0);
        const dateB = new Date(b.creationDate || b.createdAt || b.created_at || 0);
        return dateB - dateA; // Ordine decrescente (più recente prima)
      });
      
      setRequests(sortedData);
      setFilteredRequests(sortedData);
      setTotalRequests(sortedData.length);
      setLoading(false);
    } catch (error) {
      console.error("Errore nel caricamento delle richieste:", error);
      Toastify({
        text: "Errore nel caricamento delle richieste di assistenza",
        duration: 3000,
        close: true,
        gravity: "top",
        position: "right",
        backgroundColor: "#f44336",
      }).showToast();
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/D";
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  // Stile uniforme per tutte le card
  const getCardStyle = () => {
    return {
      backgroundColor: '#ffffff',
      color: '#000000',
      borderLeft: '5px solid #3b82f6' // Blu uniforme
    };
  };
  
  // Funzione per ottenere l'etichetta dello stato
  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Completata';
      case 'in_progress':
        return 'In Lavorazione';
      default:
        return 'Aperta';
    }
  };

  return (
    <div className="w-full p-3 md:p-6 max-w-7xl mx-auto">
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mt-12 md:mt-0">
          <h1 className="text-xl md:text-2xl font-bold">Le Tue Richieste di Assistenza</h1>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">
            <button 
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center justify-center" 
              onClick={() => window.location.href = '/customerAddEditAssistanceRequest'}
            >
              <i className="fi fi-br-plus mr-2"></i>
              <span className="hidden sm:inline">Crea Nuova Richiesta di Assistenza</span>
              <span className="inline sm:hidden">Nuova Richiesta</span>
            </button>
            <div className="bg-gray-500 text-white px-4 py-2 rounded-md flex items-center justify-center text-sm">
              Richieste: {totalRequests}
            </div>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">Non hai ancora nessuna richiesta di assistenza.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
          {filteredRequests.map((request) => {
            const cardStyle = getCardStyle();
            const statusBadgeClass = getStatusBadgeClass(request.status);
            const statusLabel = getStatusLabel(request.status);
            const requestCode = request.code || `A${String(Math.floor(Math.random() * 9000) + 1000)}-${new Date().getFullYear()}`;
            const priority = request.priority || (Math.random() > 0.7 ? 'alta' : (Math.random() > 0.4 ? 'media' : 'bassa'));
            const priorityLabel = getPriorityLabel(priority);
            const timeframe = getTimeframe(request);
            const address = request.address || 'via borsellino 14 43014 ramiola PR';
            const systemType = request.systemType || 'Telecamere - ABITAZIONE';
            const paymentMethod = request.paymentMethod || 'BANCOMAT / CARTA DI CREDITO';
            const warranty = request.warranty ? 'SI' : 'NO';
            const maintenance = request.maintenance ? 'SI' : 'NO';
            
            return (
              <div key={request._id}>
                <div className="bg-white rounded-lg shadow-md overflow-hidden h-full" style={{borderLeft: cardStyle.borderLeft}}>
                  {/* Header della card */}
                  <div className="flex justify-between items-center py-3 px-4 border-b border-gray-200">
                    <h5 className="font-bold text-base md:text-lg">{request.customerName || "Alessandro Viani"}</h5>
                    <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs">{requestCode}</span>
                  </div>
                  
                  {/* Subheader con stato e teleassistenza */}
                  <div className="px-3 py-2 flex justify-between items-center bg-gray-50">
                    {/* Indicatore di teleassistenza */}
                    <div>
                      {request.teleassistenza && (
                        <span className="bg-green-500 text-white px-2 py-1 rounded text-xs">
                          <i className="fi fi-br-video-camera mr-1"></i> Teleassistenza
                        </span>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded text-xs text-white ${statusBadgeClass === 'bg-success' ? 'bg-green-500' : statusBadgeClass === 'bg-warning' ? 'bg-yellow-500 text-black' : 'bg-blue-500'}`}>{statusLabel}</span>
                  </div>
                  
                  {/* Corpo della card */}
                  <div className="p-4 space-y-3">
                    {/* Sistema */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <FontAwesomeIcon icon={faTags} className="text-gray-400 text-sm" />
                        <span className="text-gray-500 text-xs">Sistema:</span>
                      </div>
                      <div className="font-semibold text-sm">{systemType}</div>
                    </div>
                    
                    {/* Indirizzo */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <i className="fi fi-br-marker text-gray-400 text-sm"></i>
                        <span className="text-gray-500 text-xs">Indirizzo:</span>
                      </div>
                      <div className="font-semibold text-sm">{address}</div>
                    </div>
                    
                    {/* Priorità e tempistiche */}
                    <div className="flex gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="text-gray-400 text-sm" />
                          <span className="text-gray-500 text-xs">Priorità:</span>
                        </div>
                        <div className="font-semibold text-sm">{priorityLabel}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <FontAwesomeIcon icon={faClock} className="text-gray-400 text-sm" />
                          <span className="text-gray-500 text-xs">Tempistica:</span>
                        </div>
                        <div className="font-semibold text-sm">{timeframe}</div>
                      </div>
                    </div>
                    
                    {/* Date */}
                    <div className="flex justify-between gap-2 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 text-sm" />
                          <span className="text-gray-500 text-xs">Scade:</span>
                        </div>
                        <div className="font-semibold text-sm">{formatDate(request.expirationDate)}</div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 text-sm" />
                          <span className="text-gray-500 text-xs">Creata:</span>
                        </div>
                        <div className="font-semibold text-sm">{formatDate(request.creationDate || request.createdAt || request.created_at)}</div>
                      </div>
                    </div>
                    
                    {/* Dettagli aggiuntivi */}
                    <div className="flex justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-1">
                        <FontAwesomeIcon icon={faTools} className="text-gray-400 text-sm" />
                        <span className="text-gray-500 text-xs">Manut.:</span>
                        <span className="font-semibold text-sm">{maintenance}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FontAwesomeIcon icon={faShieldAlt} className="text-gray-400 text-sm" />
                        <span className="text-gray-500 text-xs">Garanzia:</span>
                        <span className="font-semibold text-sm">{warranty}</span>
                      </div>
                    </div>
                    
                    {/* Pagamento */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <FontAwesomeIcon icon={faCreditCard} className="text-gray-400 text-sm" />
                        <span className="text-gray-500 text-xs">Pagamento:</span>
                      </div>
                      <div className="font-semibold text-sm">{paymentMethod}</div>
                    </div>
                    
                    {/* Note */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-sm" />
                        <span className="text-gray-500 text-xs">Note:</span>
                      </div>
                      <div className="text-sm overflow-hidden" style={{maxHeight: '3rem'}}>
                        {request.description || request.notes || "bntvbn"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}  
    </div>
  );
};

// Funzione per ottenere l'etichetta della priorità
const getPriorityLabel = (priority) => {
  switch (priority) {
    case 'alta':
    case 'high':
      return 'Alta';
    case 'media':
    case 'medium':
      return 'Media';
    case 'bassa':
    case 'low':
      return 'Bassa';
    default:
      return 'Normal';
  }
};

// Funzione per ottenere la tempistica
const getTimeframe = (request) => {
  if (request.timeframe) {
    return request.timeframe;
  }
  
  // Se non è disponibile, generiamo un valore di esempio basato sulla priorità
  const priority = request.priority || 'normal';
  switch (priority) {
    case 'alta':
    case 'high':
      return '24 ore';
    case 'media':
    case 'medium':
      return '48 ore';
    case 'bassa':
    case 'low':
      return '5 giorni';
    default:
      return '72 ore';
  }
};

// Funzione per ottenere il badge di stato
const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'completed':
      return 'bg-success';
    case 'in_progress':
      return 'bg-warning text-dark';
    default:
      return 'bg-primary';
  }
};

export default CustomerAssistanceRequests;
